import { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shell from '@/styles/authShell.module.css';
import { isOauthStateValid } from '@/lib/googleOauthState';
import { roleHome } from '@/lib/roleHome';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleCallback = () => {
  usePageMeta(PAGE_META.authCallback);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    
    const code = searchParams.get('code');
    // Read state before replaceState strips the query string below.
    const state = searchParams.get('state');
    if (code) {
      // Remove code from URL immediately to prevent re-use on page refresh
      window.history.replaceState({}, '', '/auth/google/callback');
      // Refuse a callback this tab did not start. Without this an attacker can
      // run their own Google flow and get someone else's browser to finish it,
      // signing that person into the attacker's account without them noticing.
      if (!isOauthStateValid(state)) {
        toast.error('We could not verify that sign-in. Please try again.');
        navigate('/login');
        return;
      }
      handleGoogleCode(code);
    } else {
      // No code - check if we already handled it (page refresh after successful auth)
      const token = localStorage.getItem('access_token');
      if (token) {
        // Already authenticated, redirect to dashboard
        window.location.href = '/';
      } else {
        navigate('/login');
      }
    }
  }, []);

  const handleGoogleCode = async (code) => {
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + '/auth/google/callback';
      // Pull referral_token from sessionStorage (set by ReferralClaim page) so the backend can auto-link
      const referralToken = sessionStorage.getItem('referral_token') || null;

      console.log('[GoogleCallback] Sending code to backend...');
      const response = await fetch(`${API}/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri, referral_token: referralToken })
      });

      console.log('[GoogleCallback] Response status:', response.status);
      const data = await response.json();
      console.log('[GoogleCallback] Response data:', JSON.stringify(data).substring(0, 200));

      if (data.exists && data.access_token) {
        // Existing user — log them in
        localStorage.setItem('access_token', data.access_token);
        console.log('[GoogleCallback] Token stored, role:', data.user.role);

        // If backend linked a referral application, clear the token and route to client dashboard
        if (data.referral_linked_application_id) {
          let refPrefill = {};
          try { refPrefill = JSON.parse(sessionStorage.getItem('referral_prefill') || '{}'); } catch { /* ignore */ }
          sessionStorage.removeItem('referral_token');
          sessionStorage.removeItem('referral_prefill');
          sessionStorage.setItem('referral_welcome', JSON.stringify({
            applicationId: data.referral_linked_application_id,
            attorneyName: refPrefill.lawyerName || '',
            lawFirmName: refPrefill.lawFirmName || '',
            firstName: refPrefill.firstName || data.user?.name?.split(' ')[0] || ''
          }));
          navigate('/client', { replace: true, state: { user: data.user } });
          return;
        }

        // Shared map: this one used to omit paralegal, so lawyer support staff
        // signing in with Google landed on '/' instead of the lawyer area.
        const targetPath = roleHome(data.user.role);
        // Pass user data in state so ProtectedRoute skips the auth check
        navigate(targetPath, { replace: true, state: { user: data.user } });
        return;
      } else if (!data.exists && data.google_user) {
        // New user — redirect to signup with Google info (referral_token stays in sessionStorage for SignupWizard)
        toast.info('Please complete your registration');
        navigate('/signup', {
          state: {
            fromGoogle: true,
            name: data.google_user.name,
            email: data.google_user.email,
            picture: data.google_user.picture,
            // Carried to /auth/signup as proof the address is Google-verified,
            // so the new account skips the redundant verification email.
            idToken: data.id_token
          }
        });
      } else {
        toast.error(data.detail || 'Google login failed');
        navigate('/login');
      }
    } catch (error) {
      console.error('Google callback error:', error);
      // If token is already stored (from a successful first call), just redirect
      if (localStorage.getItem('access_token')) {
        window.location.href = '/';
        return;
      }
      toast.error('Something went wrong. Please try again.');
      navigate('/login');
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <div>
            <Loader2 size={40} className={shell.spinner} aria-hidden="true" />
            <p>Signing you in with Google...</p>
          </div>
        </main>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;
